import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PensumService from '../../services/pensum.service';

const DeletedPensumsList = () => {
  const { hasPermission } = useAuth();
  const [pensums, setPensums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null, name: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeletedPensums();
  }, []);

  const fetchDeletedPensums = async () => {
    setLoading(true);
    try {
      const response = await PensumService.getTrashed();
      setPensums(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted pensums:', error);
      showSnackbar('Error loading deleted pensums: ' + (error.response?.data?.message || error.message), 'error');
      setPensums([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id, name) => {
    setRestoreDialog({ open: true, id, name });
  };

  const confirmRestore = async () => {
    try {
      await PensumService.restore(restoreDialog.id);
      showSnackbar('Pensum restored successfully');
      fetchDeletedPensums();
    } catch (error) {
      showSnackbar('Error restoring pensum', 'error');
    } finally {
      setRestoreDialog({ open: false, id: null, name: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPensums = pensums.filter(pensum =>
    pensum.curso?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pensum.grade?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/pensums')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">Deleted Pensums</Typography>
            </Box>
          </Box>

          <TextField
            placeholder="Search deleted pensums..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Curso</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Deleted At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPensums.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No deleted pensums found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPensums.map((pensum) => (
                    <TableRow key={pensum.id} hover>
                      <TableCell>
                        <Chip label={pensum.orden} color="default" variant="outlined" size="small" />
                      </TableCell>
                      <TableCell>{pensum.curso?.name || 'N/A'}</TableCell>
                      <TableCell>{pensum.grade?.name || 'N/A'}</TableCell>
                      <TableCell>{formatDate(pensum.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('pensums.edit') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(pensum.id, `${pensum.grade?.name} - ${pensum.curso?.name}`)}
                              title="Restore Pensum"
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null, name: null })}
      >
        <DialogTitle>Restore Pensum</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore <strong>{restoreDialog.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null, name: null })}>
            Cancel
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedPensumsList;
